import {
  CircularProgress,
  InputAdornment,
  IconButton,
  capitalize,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grow,
  Divider,
} from '@mui/material'
import * as React from 'react'
import { withRouter } from 'next/router'
import {
  LoginStudentRequest,
  LoginTeacherRequest,
  LoginAdminRequest,
  checkIfUserEmailIsValidRequest,
} from '../../../services/api-requests/api-request'
import { removeURLQueryParameter } from '../../../services/config'
import { WithRouterProps } from 'next/dist/client/with-router'
import { redirectOnAuthSuccess } from '../../../services/utils'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { AnalyticService } from '../../../lib/analytics/analyticService'
import { useSnackbarComponent } from '../../SnackbarComponents/useSnackbarComponent'
import SignInToSchoolFirebaseButton from '../../Firebase/GoogleSignInToSchoolFirebaseButton'
import { IUserType } from '../../../interfaces/user.interface'
import CheckedIcon from '@mui/icons-material/CheckCircle'
import MuiLink from '@mui/material/Link'
import useSchoolUserAuth from './useSchoolUserAuth'
import { trackFbPixelsRegistrationToSchoolDetails } from '../../../lib/analytics/fbPixels'

interface ILoginComponentProps extends WithRouterProps {
  school_details: any
  isDialog: boolean
  onSuccess: () => void
  goToSignup: (opts: {
    email: string
    userData?: any
    userType: IUserType | ''
  }) => void
  setErrorMessage: (errMessage: string) => void
}

// const _loginProgression = {
//   0: "",
//   1: "What kind of user are you?",
//   2: "Enter Email/UserCode",
//   3: "Enter Password with Email",
//   4: "Submit",
// };

const LoginComponent = (props: ILoginComponentProps) => {
  const [userCode, setUserCode] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [submitLoading, setSubmitLoading] = React.useState(false)
  const [userType, setUserType] = React.useState<IUserType | ''>('student')
  const [redirectUrl, setRedirectUrl] = React.useState('')
  const [sourceUrl, setSourceUrl] = React.useState('')
  const [schoolId, setSchoolId] = React.useState('')
  const [loginWith, setLoginWith] = React.useState('')
  const [authToken, setAuthToken] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [submitDisabled, setSubmitDisabled] = React.useState(false)
  const [verifiedUserEmail, setVerifiedUserEmail] = React.useState<
    'valid' | 'loading' | 'notValid' | undefined
  >(undefined)
  const [canLoginWithPassword, setCanLoginWithPassword] = React.useState<
    boolean
  >(true)

  const loginProgressionState = React.useRef<number>(1)

  const componentHasMounted = React.useRef(false)

  const snackbarHook = useSnackbarComponent()
  const schoolUserAuthHook = useSchoolUserAuth()

  const setLoginProgressionState = (value: number) => {
    loginProgressionState.current = value
  }
  const clearLoginStates = () => {
    setUserCode('')
    setPassword('')
    setAuthToken('')
    setLoginWith('')
    setUserType('')
    setSourceUrl('')
  }

  React.useEffect(() => {
    setRedirectUrl(
      props.router && props.router.query.redirect
        ? (props.router.query.redirect as string)
        : '',
    )

    setUserType(
      props.router && props.router.query.user_type
        ? (props.router.query.user_type as IUserType)
        : '',
    )

    setSchoolId(
      props.router && props.router.query.school_auth_id
        ? (props.router.query.school_auth_id as string)
        : '',
    )

    setLoginWith(
      props.router && props.router.query.login_with
        ? (props.router.query.login_with as string)
        : '',
    )

    setAuthToken(
      (props.router &&
        ((props.router.query.auth_token ||
          props.router.query.google_auth_token) as string)) ||
        '',
    )

    setSourceUrl(
      props.router && props.router.query.source
        ? (props.router.query.source as string)
        : '',
    )
  }, [])

  React.useEffect(() => {
    if (props.router?.query?.errors && props.router.query.errors == 'true') {
      const _error = `Failed to Authenticate User${
        loginWith &&
        loginWith !== 'school_api_auth' &&
        ` with ${capitalize(loginWith)}`
      }`
      props.setErrorMessage(_error)
      snackbarHook.open(_error, 'error')
      return
    }
    if (
      authToken &&
      schoolId === props.school_details._id &&
      userType &&
      loginWith
    ) {
      submitData()
      console.log({
        authToken,
        schoolId,
        userType,
        redirectUrl,
        loginWith,
        sourceUrl,
      })
      return
    }
  }, [
    authToken,
    schoolId,
    userType,
    userCode,
    redirectUrl,
    loginWith,
    sourceUrl,
  ])

  React.useEffect(() => {
    if (componentHasMounted.current) {
      enableSubmit()
    } else {
      componentHasMounted.current = true
    }
  }, [userCode, password, userType])

  const enableSubmit = () => {
    if (
      !userCode ||
      !password
      // || !userType
    ) {
      setSubmitDisabled(true)
      // props.setErrorMessage("!Some field are empty");
      return
    }

    setSubmitDisabled(false)
    props.setErrorMessage('')
  }

  const loginUser = (
    userType: string,
    opts: {
      userCode: string
      schoolId: string
      password?: string
      idToken?: string
      ignorePasswordValidation?: boolean
      loginWith: string
    },
  ) => {
    if (userType === 'student') {
      return LoginStudentRequest({
        ...opts,
      })
    } else if (userType === 'teacher') {
      return LoginTeacherRequest({
        ...opts,
      })
    } else if (userType === 'admin') {
      return LoginAdminRequest({
        ...opts,
      })
    } else {
      console.log('no user has been selected ', userType)
    }
  }

  const submitData = async (_usrType?: string) => {
    setSubmitDisabled(true)
    setSubmitLoading(true)

    const _userType = _usrType || userType

    try {
      const user_code = userCode.trim()
      const response = await loginUser(_userType, {
        userCode: user_code,
        schoolId: props.school_details._id,
        password: password,
        idToken: authToken,
        ignorePasswordValidation: [
          'school_api_auth',
          'google',
          'pledre_account_user',
        ].includes(loginWith),
        loginWith: loginWith,
      })

      console.log(response)
      const data = response?.data
      if (data.data) {
        if (data.data.continue_register_user) {
          props.goToSignup({
            email: data.data.email,
            userData: data.data,
            userType: userType,
          })
          for (let i = 0; i < Object.keys(props.router.query).length; i++) {
            const key = Object.keys(props.router.query)[i]
            if (!['redirect', 'source'].includes(key)) {
              removeURLQueryParameter(key)
            }
          }
          setSubmitLoading(false)
          return
        }
        loginSuccessful(data.data)
        snackbarHook.open('Login successful', 'success')
      }
    } catch (err: any) {
      console.log(err, err?.response)
      let _error = 'Network Error'
      if (
        err &&
        err?.response &&
        err?.response.data &&
        err?.response.data.message
      ) {
        _error = err?.response.data.message
      }
      setSubmitLoading(false)
      setSubmitDisabled(false)

      const source = sourceUrl

      if (loginWith) {
        clearLoginStates()
      }
      for (let i = 0; i < Object.keys(props.router.query).length; i++) {
        const key = Object.keys(props.router.query)[i]

        removeURLQueryParameter(key)
      }
      props.setErrorMessage(_error)
      snackbarHook.open(_error, 'error')

      setPassword('') // clear password field

      setSubmitLoading(false)

      console.log({ source })

      if (source) {
        window.location.href = source // redirect to source on fail
      }
    }
  }

  const loginSuccessful = (userData: any) => {
    schoolUserAuthHook.onAuthSuccessful(userData, () => {
      AnalyticService.onUserLogin({
        email: userData?.email,
        firstName: userData?.name,
        lastName: '',
        userId: userData?._id,
        userType: userData.user_type,
      })

      trackFbPixelsRegistrationToSchoolDetails(props.school_details)

      if (props.onSuccess) {
        props.onSuccess()
      }

      if (!props.isDialog) {
        redirectOnAuthSuccess(redirectUrl)
      }
    })
  }

  const handleUserTypeChange = (e: any) => {
    const value = e.target.value as IUserType
    setUserType(value)
    if (['student', 'teacher'].includes(value)) {
      setLoginProgressionState(2)
      setVerifiedUserEmail(undefined)
      setCanLoginWithPassword(true)
      setPassword('')
    } else {
      setLoginProgressionState(3)
      setPassword('')
    } // clear password
  }

  const handleUserCodeChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const value = e.currentTarget.value
    setUserCode(value)
    if (['teacher', 'student'].includes(userType)) {
      setLoginProgressionState(2)
      setVerifiedUserEmail(undefined)
      setCanLoginWithPassword(true)
    } else {
      setLoginProgressionState(3)
    }
  }

  const checkIfUserEmailIsValid = async () => {
    try {
      setVerifiedUserEmail('loading')
      const axiosRes = await checkIfUserEmailIsValidRequest({
        userCode,
        userType,
        schoolId: props.school_details._id,
      })
      if (axiosRes?.data?.data) {
        const valid = axiosRes?.data?.data?.valid
        const canLoginWithPassword =
          axiosRes?.data?.data?.can_login_with_password
        if (valid) {
          setVerifiedUserEmail('valid')
          setCanLoginWithPassword(canLoginWithPassword)
          setLoginProgressionState(3)
        } else {
          setVerifiedUserEmail('notValid')
        }
        return
      }
      setVerifiedUserEmail(undefined)
    } catch (e) {
      console.log(e)
      setVerifiedUserEmail(undefined)
    }
  }

  const handlePasswordChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setPassword(e.currentTarget.value)
    setLoginProgressionState(3)
  }

  return (
    <>
      {componentHasMounted.current && (
        <div>
          {props.school_details && (
            <div>
              <form>
                <FormControl
                  fullWidth={true}
                  variant="outlined"
                  style={{ margin: '10px 0' }}
                >
                  <InputLabel>Select account type</InputLabel>
                  <Select
                    style={{ minWidth: '120px', fontWeight: 500 }}
                    fullWidth={true}
                    value={userType}
                    onClick={_e => {
                      if (userType) {
                        if (['teacher', 'student'].includes(userType)) {
                          setVerifiedUserEmail(undefined)
                          setCanLoginWithPassword(true)
                          setLoginProgressionState(2)
                        } else {
                          setLoginProgressionState(3)
                        }
                      }
                    }}
                    onChange={handleUserTypeChange}
                    label={'Select account type'}
                    // disabled={loginProgressionState !== 1}
                  >
                    <MenuItem key={1} value={'student'}>
                      Student/Learner
                    </MenuItem>

                    <MenuItem key={2} value={'teacher'}>
                      Teacher/Facilitator
                    </MenuItem>
                    <MenuItem key={3} value={'admin'}>
                      Administrator/School Owner
                    </MenuItem>
                  </Select>
                </FormControl>
                <Grow
                  in={Boolean(userType) && loginProgressionState.current >= 2}
                  style={{ transformOrigin: '0' }}
                  unmountOnExit={true}
                >
                  <div>
                    {['student', 'teacher'].includes(userType) &&
                      canLoginWithPassword && (
                        <div
                          style={{
                            // margin: "10px 0",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Divider style={{ width: '30%' }} />
                          <Typography
                            variant={'body2'}
                            style={{ padding: '0 4%', fontWeight: 'bold' }}
                          >
                            Enter your email
                          </Typography>
                          <Divider style={{ width: '30%' }} />
                        </div>
                      )}
                    <TextField
                      name={'email'}
                      type={'email'}
                      color="primary"
                      fullWidth={true}
                      required={true}
                      variant="outlined"
                      label={'Email'}
                      placeholder={'Type in your email'}
                      onChange={handleUserCodeChange}
                      helperText={
                        ['student', 'teacher'].includes(userType) &&
                        verifiedUserEmail === 'notValid' && (
                          <div style={{ display: 'flex' }}>
                            <Typography
                              component={'div'}
                              variant={'inherit'}
                              style={{ fontWeight: 'bold' }}
                            >
                              You haven’t created an account yet. Try another
                              email or
                              {!(
                                userType === 'teacher' &&
                                !props.school_details?.allow_teacher_signup
                              ) ? (
                                <MuiLink
                                  onClick={() => {
                                    console.log('prefill', { userCode })
                                    props.goToSignup({
                                      email: userCode,
                                      userType: userType,
                                    })
                                  }}
                                  color="primary"
                                  style={{ marginLeft: '5px' }}
                                >
                                  Continue to sign up
                                </MuiLink>
                              ) : (
                                <Typography
                                  variant={'inherit'}
                                  style={{ fontWeight: 'bold' }}
                                  component={'p'}
                                  color={'error'}
                                >
                                  Registration to school is currently disabled
                                  for teachers. Signup as a student instead or
                                  contact the school admin
                                </Typography>
                              )}
                            </Typography>
                          </div>
                        )
                      }
                      style={{ margin: '10px 0' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <div>
                              {userType === 'student' &&
                                Boolean(userCode) &&
                                verifiedUserEmail === 'valid' && (
                                  <CheckedIcon
                                    fontSize={'small'}
                                    style={{ color: '#007E33', margin: '5px' }}
                                  />
                                )}
                            </div>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </Grow>
                <Grow
                  in={
                    (['admin'].includes(userType) && Boolean(userCode)) ||
                    (['student', 'teacher'].includes(userType) &&
                      verifiedUserEmail === 'valid' &&
                      canLoginWithPassword)
                  }
                  style={{ transformOrigin: '0' }}
                  unmountOnExit={true}
                >
                  <TextField
                    name={'password'}
                    color="primary"
                    fullWidth={true}
                    variant="outlined"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    required={true}
                    value={password}
                    onChange={handlePasswordChange}
                    helperText={
                      <Typography
                        style={{ marginBottom: '5px' }}
                        variant={'inherit'}
                      >
                        Forgot your password?
                        <MuiLink href="/reset-password" color="primary">
                          <b> Click here to reset</b>
                        </MuiLink>
                      </Typography>
                    }
                    placeholder={'******'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setShowPassword(!showPassword)
                            }}
                            size="large"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    style={{ margin: '10px 0' }}
                  />
                </Grow>
                <div style={{ display: 'flex' }}>
                  {/* <Button */}
                  {/*  disabled={loginProgressionState.current <= 1} */}
                  {/*  variant={"outlined"} */}
                  {/*  color={"primary"} */}
                  {/*  style={{ margin: "10px 5px 10px 0" }} */}
                  {/*  onClick={(e?: React.MouseEvent<{}>) => { */}
                  {/*    e?.preventDefault(); */}
                  {/*    const newValue = loginProgressionState.current - 1; */}
                  {/*    setLoginProgressionState( */}
                  {/*      newValue === 2 && userType !== "student" ? 1 : newValue */}
                  {/*    ); */}
                  {/*  }} */}
                  {/* > */}
                  {/*  <ArrowBack fontSize={"small"} /> */}
                  {/* </Button> */}
                  {Boolean(userType) && (
                    <>
                      {userType === 'student' &&
                      verifiedUserEmail === 'notValid' ? (
                        <Button
                          variant="contained"
                          style={{ margin: '10px 0px' }}
                          color="primary"
                          onClick={(e?: React.MouseEvent<{}>) => {
                            e?.preventDefault()
                            props.goToSignup({
                              email: userCode,
                              userType: userType,
                            })
                          }}
                          fullWidth={true}
                        >
                          Continue to sign up as student
                        </Button>
                      ) : userType === 'teacher' &&
                        verifiedUserEmail === 'notValid' ? (
                        <Button
                          variant="contained"
                          style={{ margin: '10px 0px' }}
                          color="primary"
                          disabled={
                            userType === 'teacher' &&
                            !props.school_details?.allow_teacher_signup
                          }
                          onClick={(e?: React.MouseEvent<{}>) => {
                            e?.preventDefault()
                            props.goToSignup({
                              email: userCode,
                              userType: userType,
                            })
                          }}
                          fullWidth={true}
                        >
                          Continue to register up as teacher
                        </Button>
                      ) : (
                        <>
                          {canLoginWithPassword && (
                            <>
                              {loginProgressionState.current < 3 ? (
                                <Button
                                  disabled={
                                    loginProgressionState.current < 2 ||
                                    !userCode ||
                                    !['teacher', 'student'].includes(
                                      userType,
                                    ) ||
                                    verifiedUserEmail === 'loading'
                                  }
                                  variant="contained"
                                  style={{ margin: '10px 0px' }}
                                  color="primary"
                                  type="submit"
                                  onClick={(e?: React.MouseEvent<{}>) => {
                                    e?.preventDefault()
                                    checkIfUserEmailIsValid()
                                  }}
                                  fullWidth={true}
                                >
                                  Next{' '}
                                  {verifiedUserEmail === 'loading' && (
                                    <CircularProgress size={20} />
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  disabled={submitLoading || submitDisabled}
                                  variant="contained"
                                  color="primary"
                                  type="submit"
                                  onClick={(e?: React.MouseEvent<{}>) => {
                                    e?.preventDefault()
                                    setSubmitLoading(true)
                                    submitData()
                                    // setOpenSelectUserDialog(true);
                                  }}
                                  fullWidth={true}
                                  style={{ margin: '10px 0' }}
                                >
                                  Login
                                </Button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <Grow
                  in={Boolean(userType)}
                  style={{ transformOrigin: '0' }}
                  unmountOnExit={true}
                >
                  <div>
                    {!['student', 'teacher'].includes(userType) ||
                      (['student', 'teacher'].includes(userType) &&
                        canLoginWithPassword && (
                          <div
                            style={{
                              // margin: "10px 0",
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Divider style={{ width: '40%' }} />
                            <Typography
                              variant={'body2'}
                              style={{ padding: '0 5%', fontWeight: 'bold' }}
                            >
                              OR
                            </Typography>
                            <Divider style={{ width: '40%' }} />
                          </div>
                        ))}
                    {!(
                      userType === 'teacher' && verifiedUserEmail === 'notValid'
                    ) && (
                      <div style={{ margin: '10px 0px' }}>
                        <SignInToSchoolFirebaseButton
                          text={
                            ['student', 'teacher'].includes(userType)
                              ? 'Authenticate with Google'
                              : 'Sign in with Google'
                          }
                          schoolDetails={props.school_details}
                          onLoginSuccessful={loginSuccessful}
                          snackbarHook={snackbarHook}
                          userType={userType}
                          isDialog={props.isDialog}
                        />
                      </div>
                    )}
                    {['teacher', 'student'].includes(userType) &&
                      verifiedUserEmail === 'valid' &&
                      !canLoginWithPassword && (
                        <Typography
                          style={{ marginBottom: '5px' }}
                          variant={'body2'}
                        >
                          <MuiLink href="/reset-password" color="primary">
                            <b>Click here to reset your password</b>
                          </MuiLink>
                        </Typography>
                      )}
                  </div>
                </Grow>
                <div>
                  {submitLoading && (
                    <div>
                      <div
                        style={{
                          position: 'fixed',
                          width: '100%',
                          height: '100%',
                          top: '0px',
                          left: '0px',
                          background: 'black',
                          opacity: 0.3,
                          zIndex: 5,
                        }}
                      />
                      <div
                        style={{
                          position: 'fixed',
                          width: '100%',
                          height: '100%',
                          top: '0px',
                          left: '0px',
                          paddingTop: '50vh',
                          textAlign: 'center',
                          zIndex: 6,
                        }}
                      >
                        <CircularProgress size={40} />
                      </div>
                    </div>
                  )}
                </div>
                {/* <YouAreLoggedInDialogComponent
                open={openYouareLoggedInDialog}
              /> */}
              </form>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default withRouter(LoginComponent)
